import { Empty } from 'antd';
import ProductOffer from 'model/ProductOffer';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetRandomOffersQuery } from 'services/ecommerceApi';
import { sessionSelectors } from 'store/state/selectors';
import * as S from './styles';

interface OffersByCategorySectionProps {
  title: string;
  offerTypes?: string[];
  icon?: string;
  autoScroll?: boolean;
  isAuthenticated: boolean;
  badgeText?: string;
  isMyOffers?: boolean;
}

function ProductCardSkeleton() {
  return (
    <S.SkeletonCard>
      <S.SkeletonImage />
      <S.SkeletonContent>
        <S.SkeletonLine height="20px" />
        <S.SkeletonLine width="70%" height="14px" />
      </S.SkeletonContent>
    </S.SkeletonCard>
  );
}

export default function OffersByCategorySection({
  title,
  offerTypes,
  icon,
  autoScroll = false,
  isAuthenticated,
  badgeText,
  isMyOffers = false,
}: OffersByCategorySectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const producerUrl = useSelector(sessionSelectors.getProducerUrl);
  const siteIds = useSelector(sessionSelectors.getSitesIds);
  const customerId = useSelector(sessionSelectors.getSelectedCustomerId);
  const deliveryDate = useSelector(sessionSelectors.getSelectedDeliveryDate);
  const billingDate = useSelector(sessionSelectors.getSelectedBillingDate);

  const isLkpTab = offerTypes?.includes('inTransit') ||
    offerTypes?.includes('received');

  const { data: offers, isLoading } = useGetRandomOffersQuery({
    siteId: siteIds,
    customerId,
    deliveryDate: deliveryDate || undefined,
    businessDate: billingDate || undefined,
    offerTypes,
    isMyOffers: isMyOffers || undefined,
    page: 1,
    totalPage: 20,
    includeGfpImages: isLkpTab || false,
  });

  useEffect(() => {
    if (offers?.offers) {
      console.log(`[${title}] Produtos carregados:`, offers.offers.length);
      console.log(`[${title}] Primeiro produto:`, {
        name: offers.offers[0]?.name,
        defaultImage: offers.offers[0]?.defaultImage,
        images: offers.offers[0]?.images,
        imagesFormatted: offers.offers[0]?.imagesFormatted,
      });
    }
  }, [offers, title]);

  const handleProductClick = (product: ProductOffer) => {
    if (!isAuthenticated) {
      return;
    }

    const packingSelected = product.packings?.[0];
    if (packingSelected) {
      navigate(
        `/${producerUrl}/offer-details/${product.offerId}/${product.offerType}/${packingSelected.id}/0`,
      );
    } else {
      navigate(`/${producerUrl}/products`);
    }
  };

  const productsToShow = useMemo(() => {
    if (!offers?.offers) return [];

    let filteredOffers = offers.offers;
    if (!isAuthenticated) {
      filteredOffers = offers.offers.filter((offer) => offer.offerType !== 3);
    }

    if (autoScroll) {
      const products = filteredOffers.slice(0, 20);
      return [...products, ...products];
    }
    return filteredOffers.slice(0, 20);
  }, [offers, autoScroll, isAuthenticated]);

  useEffect(() => {
    if (
      !autoScroll ||
      !scrollContainerRef.current ||
      productsToShow.length === 0
    ) {
      return;
    }

    const container = scrollContainerRef.current;
    let currentScrollPosition = 0;
    let isPaused = false;

    const cardWidth = 200 + 16;
    const visibleCards = Math.floor(container.offsetWidth / cardWidth);
    const scrollStep = visibleCards * cardWidth;
    const halfWidth = (productsToShow.length / 2) * cardWidth;

    const scrollToNext = () => {
      if (!isPaused && container) {
        currentScrollPosition += scrollStep;

        if (currentScrollPosition >= halfWidth) {
          container.scrollTo({
            left: 0,
            behavior: 'auto',
          });
          currentScrollPosition = scrollStep;
        }

        container.scrollTo({
          left: currentScrollPosition,
          behavior: 'smooth',
        });
      }
    };

    const handleMouseEnter = () => {
      isPaused = true;
    };

    const handleMouseLeave = () => {
      isPaused = false;
    };

    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    const intervalId = setInterval(scrollToNext, 5000);

    return () => {
      clearInterval(intervalId);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [autoScroll, productsToShow]);

  if (isLoading) {
    return (
      <S.SectionContainer>
        <S.SectionHeader>
          <S.SectionTitle>{title}</S.SectionTitle>
          <S.Divider />
        </S.SectionHeader>
        <S.LoadingContainer>
          {[...Array(12)].map((_, index) => (
            <ProductCardSkeleton key={`skeleton-${index}`} />
          ))}
        </S.LoadingContainer>
      </S.SectionContainer>
    );
  }

  if (!productsToShow || productsToShow.length === 0) {
    return (
      <S.SectionContainer>
        <S.SectionHeader>
          <S.SectionTitle>{title}</S.SectionTitle>
          <S.Divider />
        </S.SectionHeader>
        <S.EmptyContainer>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Nenhum produto encontrado"
          />
        </S.EmptyContainer>
      </S.SectionContainer>
    );
  }

  return (
    <S.SectionContainer>
      <S.SectionHeader>
        <S.SectionTitle>
          {icon && <S.TitleIcon>{icon}</S.TitleIcon>}
          {title}
        </S.SectionTitle>
        <S.Divider />
      </S.SectionHeader>
      <S.ScrollContainer ref={scrollContainerRef} $autoScroll={autoScroll}>
        <S.ProductGrid $autoScroll={autoScroll}>
          {productsToShow.map((product: ProductOffer, index) => (
            <S.ProductCard
              key={`offer-${product.offerId}-${product.id}-${index}`}
              onClick={() => handleProductClick(product)}
              $isClickable={isAuthenticated}
            >
              <S.ProductImageContainer>
                {product.defaultImage ||
                product.images?.[0] ||
                product.imagesFormatted?.[0] ? (
                  <S.ProductImage
                    src={
                      product.defaultImage ||
                      product.images?.[0] ||
                      product.imagesFormatted?.[0] ||
                      ''
                    }
                    alt={product.name}
                    loading="lazy"
                    onError={(e) => {
                      console.error('Falha ao carregar imagem:', {
                        product: product.name,
                        defaultImage: product.defaultImage,
                        images: product.images,
                        imagesFormatted: product.imagesFormatted,
                      });
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <S.ImagePlaceholder>
                    <S.PlaceholderIcon>🌸</S.PlaceholderIcon>
                  </S.ImagePlaceholder>
                )}
                {product.offerType === 3 && badgeText && (
                  <S.ExclusiveBadge>{badgeText}</S.ExclusiveBadge>
                )}
              </S.ProductImageContainer>
              <S.ProductInfo>
                <S.ProductName>{product.longName}</S.ProductName>
                <S.ConceptName>{product.conceptName}</S.ConceptName>
                <S.ProducerName>
                  <S.ProducerLabel>Produtor:</S.ProducerLabel>
                  <S.ProducerValue>{product.siteName}</S.ProducerValue>
                </S.ProducerName>
                {isAuthenticated && product.price && (
                  <S.PriceContainer>
                    <S.PriceLabel>Preço:</S.PriceLabel>
                    <S.PriceValue>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(product.price)}
                    </S.PriceValue>
                  </S.PriceContainer>
                )}
              </S.ProductInfo>
            </S.ProductCard>
          ))}
        </S.ProductGrid>
      </S.ScrollContainer>
    </S.SectionContainer>
  );
}
